import { Component, Input, Output, EventEmitter, signal, computed, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '../material/material.module';
import { City } from '../services/city.service';
import { chipAnimation, listStagger, fadeIn } from '../../advanced-search/shared/animations';

/**
 * CityChipsSelectorComponent
 * 
 * Componente para seleção visual de cidades usando chips interativos.
 * Permite selecionar/desselecionar cidades individualmente ou em grupo.
 * 
 * Requisitos: 11.3 - Animar adição/remoção de chips
 * 
 * @example
 * ```html
 * <app-city-chips-selector
 *   [availableCities]="nearbyCities"
 *   [selectedCities]="selectedCities"
 *   (selectionChange)="onCitySelectionChange($event)">
 * </app-city-chips-selector>
 * ```
 */
@Component({
  selector: 'app-city-chips-selector',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './city-chips-selector.component.html',
  styleUrls: ['./city-chips-selector.component.scss'],
  animations: [chipAnimation, listStagger, fadeIn]
})
export class CityChipsSelectorComponent {
  /**
   * Lista de cidades disponíveis para seleção
   */
  @Input() set availableCities(cities: City[]) {
    this._availableCities.set(cities || []);
  }

  /**
   * Lista de cidades já selecionadas
   */
  @Input() set selectedCities(cities: City[]) {
    this._selectedCities.set(cities || []);
  }

  /**
   * Evento emitido quando a seleção de cidades muda
   */
  @Output() selectionChange = new EventEmitter<City[]>();

  // Signals para gerenciamento de estado reativo
  private _availableCities = signal<City[]>([]);
  private _selectedCities = signal<City[]>([]);

  /**
   * Cidades disponíveis (não selecionadas)
   */
  availableCitiesSignal = computed(() => {
    const available = this._availableCities();
    const selected = this._selectedCities();
    
    return available.filter(city => 
      !selected.some(s => s.name === city.name && s.state === city.state)
    );
  });

  /**
   * Cidades selecionadas
   */
  selectedCitiesSignal = computed(() => this._selectedCities());

  /**
   * Indica se todas as cidades disponíveis estão selecionadas
   */
  allSelected = computed(() => {
    const available = this.availableCitiesSignal();
    return available.length === 0 && this._selectedCities().length > 0;
  });

  /**
   * Conta o número de cidades selecionadas
   */
  selectedCount = computed(() => this._selectedCities().length);

  /**
   * Alterna a seleção de uma cidade específica
   */
  toggleCity(city: City): void {
    const currentSelected = this._selectedCities();
    const index = currentSelected.findIndex(
      c => c.name === city.name && c.state === city.state
    );

    let newSelected: City[];
    if (index === -1) {
      // Adiciona a cidade
      newSelected = [...currentSelected, city];
    } else {
      // Remove a cidade
      newSelected = currentSelected.filter((_, i) => i !== index);
    }

    this._selectedCities.set(newSelected);
    this.selectionChange.emit(newSelected);
  }

  /**
   * Remove uma cidade específica da seleção
   */
  removeCity(city: City): void {
    const currentSelected = this._selectedCities();
    const newSelected = currentSelected.filter(
      c => !(c.name === city.name && c.state === city.state)
    );

    this._selectedCities.set(newSelected);
    this.selectionChange.emit(newSelected);
  }

  /**
   * Seleciona todas as cidades disponíveis
   */
  selectAll(): void {
    const available = this.availableCitiesSignal();
    const currentSelected = this._selectedCities();
    const newSelected = [...currentSelected, ...available];

    this._selectedCities.set(newSelected);
    this.selectionChange.emit(newSelected);
  }

  /**
   * Remove todas as seleções
   */
  clearAll(): void {
    this._selectedCities.set([]);
    this.selectionChange.emit([]);
  }

  /**
   * Alterna entre selecionar todas ou limpar todas
   */
  toggleSelectAll(): void {
    if (this.allSelected()) {
      this.clearAll();
    } else {
      this.selectAll();
    }
  }

  /**
   * Verifica se uma cidade está selecionada
   */
  isCitySelected(city: City): boolean {
    return this._selectedCities().some(
      c => c.name === city.name && c.state === city.state
    );
  }

  /**
   * Formata a distância para exibição
   */
  formatDistance(distance: number | undefined): string {
    if (distance === undefined) return '';
    return `${distance.toFixed(0)} km`;
  }
}
